/**
 * EXPEDIENTE del alumno (F3-D) — el registro académico. Header (foto/nombre/
 * cédula/sede/estado/contacto) + pestañas: Matrículas, Asistencia, Certificados,
 * Representantes, Notas y Ficha.
 *
 * **Plan 06 · E4:** el expediente (con cédula, teléfono, dirección y contacto de
 * emergencia) y los vínculos de representante se leen y se escriben por REST.
 * Antes bajaban en `org_admin_scope`/`campus_admin_scope` —los 2 000 de la
 * academia al móvil de cada administrador— y se editaban con un `update` local
 * que la cola de sync subía. Con las filas fuera del SQLite, ese `update` no
 * encontraba fila: la edición se perdía en silencio.
 *
 * Siguen siendo local-first las pestañas que leen datos del propio alumno que ya
 * sincronizan por otros streams: matrículas, asistencia y certificados.
 *
 * La edición se gatea por capacidad (org_rbac) en la UI y la re-valida el
 * servidor. Guard de aislamiento multi-org (`belongsToActiveOrg`).
 */
import { type Component, createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  attendanceStatsOf,
  type CountryPack,
  getCountryPack,
  handleApiResponse,
  hydrateDirectory,
  parseCustomFields,
  renderIdentityFields,
  type StudentRecord,
  type StudentRecordPatch,
} from "@space/shared";
import { BadgeCheck, GraduationCap, IdCard } from "lucide-solid";
import { api } from "@/lib/api";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer } from "@/components/ui/page-container";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PillTabPanels, PillTabs } from "@/components/ui/pill-tabs";
import { NotFoundState } from "@/components/ui/not-found-state";
import { showToast } from "@/components/ui/toast";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { belongsToActiveOrg, useActiveOrg } from "../../hooks/use-active-org";
import { useOrgSettings } from "../../hooks/use-academy-data";
import {
  useMyOrgRole,
  useOrgDirectory,
  useOrgGuardianLinks,
  useSaveStudentRecord,
  useStudentRecord,
  useUserCertificates,
  useUserEnrollments,
} from "../../hooks/use-people-data";
import { useOrgIdentities } from "../../hooks/use-org-identities";

type Tab = "enrollments" | "attendance" | "certificates" | "guardians" | "notes" | "ficha";

const TABS: { id: Tab; label: MessageDescriptor }[] = [
  { id: "enrollments", label: msg`Enrollments` },
  { id: "attendance", label: msg`Attendance` },
  { id: "certificates", label: msg`Certificates` },
  { id: "guardians", label: msg`Guardians` },
  { id: "notes", label: msg`Notes` },
  { id: "ficha", label: msg`Record` },
];

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

export const StudentExpedienteView: Component<{ userId: string }> = (props) => {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const { activeOrgId } = useActiveOrg();
  // D3 (plan 06 · E2): la identidad viene de la fuente única, no de un join
  // local. `email` puede ser null porque el servidor lo redactó por capacidad.
  const people = useOrgIdentities(activeOrgId);
  const identity = () => people.identityOf(props.userId);
  const profile = useStudentRecord(activeOrgId, () => props.userId);
  const saveRecord = useSaveStudentRecord(activeOrgId, () => props.userId);
  const settings = useOrgSettings(activeOrgId);
  const rbac = useMyOrgRole(activeOrgId);
  const [tab, setTab] = createSignal<Tab>("enrollments");
  const tabItems = createMemo(() => TABS.map((td) => ({ id: td.id, label: t(td.label) })));

  const pack = () => getCountryPack(settings.data?.country_pack);
  const canManage = () => rbac.can("ManageStudents");

  const inActiveOrg = createMemo(
    () =>
      !profile.data || belongsToActiveOrg(profile.data.organizationId ?? undefined, activeOrgId()),
  );

  // LF-B: un userId desconocido/sin acceso renderizaba un expediente "fantasma"
  // (avatar con el uuid, tabs vacías). Resolución canónica sobre la identidad.
  const resolution = createEntityResolution({
    db,
    hasEntity: () => Boolean(identity()),
    isPending: () => people.isLoading(),
  });

  const displayName = () => identity()?.name ?? identity()?.email ?? props.userId;

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-5">
        <Link to="/academy/people" class="text-sm text-muted-foreground hover:text-foreground">
          ← {t(msg`People`)}
        </Link>

        <Show
          when={inActiveOrg()}
          fallback={
            <NotFoundState
              variant="no-access"
              title={t(msg`Not in this academy`)}
              description={t(msg`This record belongs to another organization.`)}
            />
          }
        >
          <Show
            when={identity()}
            fallback={
              <Show
                when={resolution() !== "loading"}
                fallback={<Skeleton class="h-32 w-full rounded-2xl" />}
              >
                <div data-testid="expediente-not-found">
                  <NotFoundState
                    variant={resolution() === "offline" ? "offline" : "no-access"}
                    title={t(msg`Person not available`)}
                    description={
                      resolution() === "offline"
                        ? t(
                            msg`You're offline and this record hasn't synced to this device yet. Reconnect and try again.`,
                          )
                        : t(msg`This person is not in this academy, or you don't have access.`)
                    }
                  />
                </div>
              </Show>
            }
          >
            {/* Header */}
            <div
              class="flex flex-wrap items-center gap-4 rounded-2xl border border-border p-5"
              data-testid="expediente-header"
            >
              <Show
                when={identity()?.image}
                fallback={
                  <div class="flex h-16 w-16 items-center justify-center rounded-full bg-accent/15 text-xl font-bold text-accent">
                    {displayName().slice(0, 2).toUpperCase()}
                  </div>
                }
              >
                {(img) => <img src={img()} alt="" class="h-16 w-16 rounded-full object-cover" />}
              </Show>
              <div class="min-w-0 flex-1">
                <h1 class="font-display text-2xl font-extrabold" data-testid="expediente-name">
                  {displayName()}
                </h1>
                <p class="text-sm text-muted-foreground">
                  <Show when={profile.data?.nationalId}>
                    {(id) => (
                      <>
                        {pack().idLabel}: {id()} ·{" "}
                      </>
                    )}
                  </Show>
                  {identity()?.email}
                </p>
                <div class="mt-1 flex flex-wrap gap-2">
                  <Show when={profile.data?.status}>
                    {(s) => (
                      <span class="rounded-full bg-accent/12 px-2 py-0.5 text-xs font-bold text-accent">
                        {s()}
                      </span>
                    )}
                  </Show>
                  <span class="rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">
                    {identity()?.role ?? ""}
                  </span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <PillTabs
              tabs={tabItems()}
              value={tab()}
              onChange={setTab}
              testIdPrefix="expediente-tab"
              label={t(msg`Student record sections`)}
            />

            <PillTabPanels value={tab()} testIdPrefix="expediente-tab">
              <Show when={tab() === "enrollments"}>
                <EnrollmentsTab userId={props.userId} />
              </Show>
              <Show when={tab() === "attendance"}>
                <AttendanceTab userId={props.userId} />
              </Show>
              <Show when={tab() === "certificates"}>
                <CertificatesTab userId={props.userId} />
              </Show>
              <Show when={tab() === "guardians"}>
                <GuardiansTab
                  userId={props.userId}
                  organizationId={activeOrgId() ?? ""}
                  canManage={canManage()}
                />
              </Show>
              <Show when={tab() === "notes"}>
                <NotesTab
                  hasRecord={Boolean(profile.data?.id)}
                  notes={profile.data?.notes ?? ""}
                  canManage={canManage()}
                  onSave={(notes) => saveRecord.mutate({ notes })}
                />
              </Show>
              <Show when={tab() === "ficha"}>
                <FichaTab
                  profile={profile.data}
                  pack={pack()}
                  canManage={canManage()}
                  onSave={(patch) => saveRecord.mutate(patch)}
                />
              </Show>
            </PillTabPanels>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};

// ─── Matrículas ──────────────────────────────────────────────────────────────

const EnrollmentsTab: Component<{ userId: string }> = (props) => {
  const { t } = useI18n();
  const enrollments = useUserEnrollments(() => props.userId);
  return (
    <div class="space-y-2" data-testid="expediente-enrollments">
      <Show
        when={(enrollments.data ?? []).length > 0}
        fallback={<p class="text-sm text-muted-foreground">{t(msg`No enrollments yet.`)}</p>}
      >
        <For each={enrollments.data ?? []}>
          {(e) => (
            <div
              class="flex items-center justify-between rounded-xl border border-border p-3"
              data-testid="enrollment-row"
            >
              <div>
                <p class="text-sm font-semibold">{e.cohortName ?? e.cohortCode ?? e.cohortId}</p>
                <p class="text-xs text-muted-foreground">{e.status}</p>
              </div>
              <Show
                when={e.finalGrade != null}
                fallback={<span class="text-xs text-muted-foreground">—</span>}
              >
                <span class="rounded-full bg-success/15 px-2 py-0.5 text-sm font-bold text-success">
                  {e.finalGrade}
                  <Show when={e.finalGradeScheme}> ({e.finalGradeScheme})</Show>
                </span>
              </Show>
            </div>
          )}
        </For>
      </Show>
    </div>
  );
};

// ─── Asistencia (columnas F3-A; graceful si null) ────────────────────────────

const AttendanceTab: Component<{ userId: string }> = (props) => {
  const { t } = useI18n();
  const enrollments = useUserEnrollments(() => props.userId);
  const rows = () =>
    (enrollments.data ?? []).map((e) => ({
      e,
      stats: attendanceStatsOf(e.raw as Record<string, unknown>),
    }));
  const anyData = () => rows().some((r) => r.stats.hasData);

  return (
    <div class="space-y-2" data-testid="expediente-attendance">
      <Show
        when={anyData()}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="attendance-empty">
            {t(msg`Attendance statistics aren't available yet.`)}
          </p>
        }
      >
        <For each={rows()}>
          {({ e, stats }) => (
            <div class="rounded-xl border border-border p-3" data-testid="attendance-row">
              <p class="text-sm font-semibold">{e.cohortName ?? e.cohortId}</p>
              <div class="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <Show when={stats.rate != null}>
                  <span class="font-bold text-foreground">{stats.rate}%</span>
                </Show>
                <span>
                  {t(msg`Present`)}: {stats.present ?? "—"}
                </span>
                <span>
                  {t(msg`Absent`)}: {stats.absent ?? "—"}
                </span>
                <span>
                  {t(msg`Late`)}: {stats.late ?? "—"}
                </span>
                <span>
                  {t(msg`Excused`)}: {stats.excused ?? "—"}
                </span>
              </div>
            </div>
          )}
        </For>
      </Show>
    </div>
  );
};

// ─── Certificados ────────────────────────────────────────────────────────────

const CertificatesTab: Component<{ userId: string }> = (props) => {
  const { t } = useI18n();
  const certs = useUserCertificates(() => props.userId);
  return (
    <div class="space-y-2" data-testid="expediente-certificates">
      <Show
        when={(certs.data ?? []).length > 0}
        fallback={<p class="text-sm text-muted-foreground">{t(msg`No certificates.`)}</p>}
      >
        <For each={certs.data ?? []}>
          {(c) => (
            <Link
              to={`/verify/${c.id}`}
              class="flex items-center justify-between rounded-xl border border-border p-3 hover:border-primary/50"
              data-testid="certificate-row"
            >
              <div class="flex items-center gap-2">
                <BadgeCheck class="h-4 w-4 text-success" />
                <span class="text-sm font-semibold">{c.course_title}</span>
              </div>
              <Show
                when={c.revoked_at}
                fallback={<span class="text-xs text-success">{t(msg`Valid`)}</span>}
              >
                <span class="text-xs text-danger">{t(msg`Revoked`)}</span>
              </Show>
            </Link>
          )}
        </For>
      </Show>
    </div>
  );
};

// ─── Representantes ──────────────────────────────────────────────────────────

const GuardiansTab: Component<{ userId: string; organizationId: string; canManage: boolean }> = (
  props,
) => {
  const { t } = useI18n();
  const links = useOrgGuardianLinks(() => props.organizationId);
  const directory = useOrgDirectory(() => props.organizationId);
  // D3: el nombre del representante sale de la fuente única de identidad.
  const people = useOrgIdentities(() => props.organizationId);
  const [guardianId, setGuardianId] = createSignal("");
  const [relationship, setRelationship] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  const wardLinks = () => (links.data ?? []).filter((l) => l.studentUserId === props.userId);
  const nameOf = (uid: string | null) => people.identityOf(uid ?? undefined)?.name ?? uid ?? "";
  const candidates = () =>
    hydrateDirectory(directory.data ?? [], people.identities()).filter(
      (r) => r.userId && r.userId !== props.userId && r.role === "guardian",
    );

  const add = async () => {
    if (!guardianId() || busy()) {
      return;
    }
    setBusy(true);
    try {
      await handleApiResponse(
        api.POST("/api/org/addGuardian", {
          body: {
            organizationId: props.organizationId,
            guardianUserId: guardianId(),
            studentUserId: props.userId,
            relationship: relationship() || null,
            isPrimary: wardLinks().length === 0,
          },
        }),
      );
      showToast(t(msg`Guardian linked`), "success");
      setGuardianId("");
      setRelationship("");
      void links.refetch?.();
    } catch {
      showToast(t(msg`Could not link the guardian`), "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await handleApiResponse(api.POST("/api/org/removeGuardian", { body: { id } }));
      void links.refetch?.();
    } catch {
      showToast(t(msg`Could not remove the guardian`), "error");
    }
  };

  return (
    <div class="space-y-3" data-testid="expediente-guardians">
      <Show
        when={wardLinks().length > 0}
        fallback={<p class="text-sm text-muted-foreground">{t(msg`No guardians linked.`)}</p>}
      >
        <ul class="space-y-2">
          <For each={wardLinks()}>
            {(l) => (
              <li
                class="flex items-center justify-between rounded-xl border border-border p-3"
                data-testid="guardian-row"
              >
                <div>
                  <p class="text-sm font-semibold">{nameOf(l.guardianUserId)}</p>
                  <p class="text-xs text-muted-foreground">
                    {l.relationship ?? t(msg`Guardian`)}
                    <Show when={l.isPrimary}> · {t(msg`Primary`)}</Show>
                  </p>
                </div>
                <Show when={props.canManage}>
                  <button
                    type="button"
                    data-testid="guardian-remove"
                    onClick={() => void remove(l.id)}
                    class="text-xs text-muted-foreground hover:text-danger"
                  >
                    {t(msg`Remove`)}
                  </button>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={props.canManage}>
        <div class="flex flex-wrap items-end gap-2 rounded-xl border border-dashed border-border p-3">
          <label class="flex-1 space-y-1 text-xs text-muted-foreground">
            {t(msg`Guardian`)}
            <select
              class={field}
              data-testid="guardian-select"
              value={guardianId()}
              onChange={(e) => setGuardianId(e.currentTarget.value)}
            >
              <option value="">{t(msg`Choose a guardian…`)}</option>
              <For each={candidates()}>
                {(c) => <option value={c.userId ?? ""}>{c.name ?? c.email ?? c.userId}</option>}
              </For>
            </select>
          </label>
          <label class="flex-1 space-y-1 text-xs text-muted-foreground">
            {t(msg`Relationship`)}
            <input
              class={field}
              data-testid="guardian-relationship"
              value={relationship()}
              onInput={(e) => setRelationship(e.currentTarget.value)}
              placeholder={t(msg`e.g. Mother`)}
            />
          </label>
          <Button
            data-testid="guardian-add"
            disabled={!guardianId() || busy()}
            onClick={() => void add()}
          >
            {t(msg`Link`)}
          </Button>
        </div>
      </Show>
    </div>
  );
};

// ─── Notas ───────────────────────────────────────────────────────────────────

const NotesTab: Component<{
  hasRecord: boolean;
  notes: string;
  canManage: boolean;
  onSave: (notes: string) => void;
}> = (props) => {
  const { t } = useI18n();
  const [value, setValue] = createSignal("");
  const [dirty, setDirty] = createSignal(false);
  // Sincroniza el valor cargado hasta que el usuario empieza a editar (evita
  // pisar ediciones locales cuando el perfil se sincroniza en segundo plano).
  createEffect(() => {
    const incoming = props.notes;
    if (!dirty()) {
      setValue(incoming);
    }
  });

  const save = () => {
    props.onSave(value());
    showToast(t(msg`Notes saved`), "success");
  };

  return (
    <div class="space-y-3" data-testid="expediente-notes">
      <Show
        when={props.hasRecord}
        fallback={
          <p class="text-sm text-muted-foreground">
            {t(msg`Create the record first (Record tab).`)}
          </p>
        }
      >
        <textarea
          class={`${field} min-h-[160px] resize-y`}
          data-testid="notes-input"
          disabled={!props.canManage}
          value={value()}
          onInput={(e) => {
            setDirty(true);
            setValue(e.currentTarget.value);
          }}
          placeholder={t(msg`Observations, follow-ups…`)}
        />
        <Show when={props.canManage}>
          <div class="flex justify-end">
            <Button data-testid="notes-save" onClick={save}>
              {t(msg`Save notes`)}
            </Button>
          </div>
        </Show>
      </Show>
    </div>
  );
};

// ─── Ficha ───────────────────────────────────────────────────────────────────

const FichaTab: Component<{
  profile: StudentRecord | undefined;
  pack: CountryPack;
  canManage: boolean;
  onSave: (patch: StudentRecordPatch) => void;
}> = (props) => {
  const { t } = useI18n();

  const emergency = createMemo(() => {
    // `parseCustomFields` normaliza las dos formas de la misma columna: objeto
    // (del REST) o texto (del espejo local de MI propio expediente).
    const ec = parseCustomFields(props.profile?.emergencyContact);
    return {
      name: ec.name ?? "",
      phone: ec.phone ?? "",
      relationship: ec.relationship ?? "",
    };
  });

  const custom = createMemo(() => parseCustomFields(props.profile?.customFields));

  // Patch DISPERSO: se manda SOLO el campo tocado. Mismo contrato que tenía el
  // `set()` de Drizzle, por eso el formulario no ha cambiado de forma.
  const save = (patch: StudentRecordPatch) => {
    props.onSave(patch);
  };

  const saveEmergency = (
    partial: Partial<{ name: string; phone: string; relationship: string }>,
  ) => {
    save({ emergencyContact: { ...emergency(), ...partial } });
  };

  const identityFields = () =>
    renderIdentityFields(props.pack, {
      nationalId: props.profile?.nationalId,
      customFields: props.profile?.customFields,
    });

  return (
    <Show
      when={props.profile?.id}
      fallback={<CreateExpedienteCta canManage={props.canManage} onCreate={() => save({})} />}
    >
      <div class="space-y-4" data-testid="expediente-ficha">
        {/* Resumen de identidad (etiquetas del country pack) */}
        <div class="rounded-xl border border-border p-4">
          <For each={identityFields()}>
            {(f) => (
              <div class="flex justify-between border-b border-border/50 py-1 text-sm last:border-0">
                <span class="text-muted-foreground">{f.label}</span>
                <span class="font-medium">{f.value}</span>
              </div>
            )}
          </For>
        </div>

        <fieldset class="space-y-3" disabled={!props.canManage}>
          <div class="grid grid-cols-2 gap-3">
            <label class="space-y-1 text-xs text-muted-foreground">
              {props.pack.idLabel}
              <input
                class={field}
                data-testid="ficha-national-id"
                value={props.profile?.nationalId ?? ""}
                onChange={(e) => save({ nationalId: e.currentTarget.value || null })}
              />
            </label>
            <label class="space-y-1 text-xs text-muted-foreground">
              {t(msg`Birth date`)}
              <input
                type="date"
                class={field}
                data-testid="ficha-birth-date"
                value={props.profile?.birthDate ?? ""}
                onChange={(e) => save({ birthDate: e.currentTarget.value || null })}
              />
            </label>
            <label class="space-y-1 text-xs text-muted-foreground">
              {t(msg`Phone`)}
              <input
                class={field}
                data-testid="ficha-phone"
                value={props.profile?.phone ?? ""}
                onChange={(e) => save({ phone: e.currentTarget.value || null })}
              />
            </label>
            <label class="space-y-1 text-xs text-muted-foreground">
              {t(msg`Status`)}
              <select
                class={field}
                data-testid="ficha-status"
                value={props.profile?.status ?? "active"}
                onChange={(e) => save({ status: e.currentTarget.value })}
              >
                {/* Los CUATRO estados del CHECK de la migración 00053. `suspended`
                    estaba aquí y NO existe en la base: elegirlo reventaba el
                    guardado (antes en silencio, por el write-path; ahora sería un
                    422 del servidor). El estado real es `withdrawn`. */}
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="graduated">graduated</option>
                <option value="withdrawn">withdrawn</option>
              </select>
            </label>
          </div>
          <label class="block space-y-1 text-xs text-muted-foreground">
            {t(msg`Address`)}
            <input
              class={field}
              data-testid="ficha-address"
              value={props.profile?.address ?? ""}
              onChange={(e) => save({ address: e.currentTarget.value || null })}
            />
          </label>

          <p class="pt-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t(msg`Emergency contact`)}
          </p>
          <div class="grid grid-cols-3 gap-3">
            <input
              class={field}
              data-testid="ficha-emergency-name"
              placeholder={t(msg`Name`)}
              value={emergency().name}
              onChange={(e) => saveEmergency({ name: e.currentTarget.value })}
            />
            <input
              class={field}
              data-testid="ficha-emergency-phone"
              placeholder={t(msg`Phone`)}
              value={emergency().phone}
              onChange={(e) => saveEmergency({ phone: e.currentTarget.value })}
            />
            <input
              class={field}
              placeholder={t(msg`Relationship`)}
              value={emergency().relationship}
              onChange={(e) => saveEmergency({ relationship: e.currentTarget.value })}
            />
          </div>

          {/* Campos personalizados existentes (del country pack / org) */}
          <Show when={Object.keys(custom()).length > 0}>
            <p class="pt-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t(msg`Custom fields`)}
            </p>
            <For each={Object.entries(custom())}>
              {([key, val]) => (
                <label class="block space-y-1 text-xs text-muted-foreground">
                  {key}
                  <input
                    class={field}
                    value={val}
                    onChange={(e) =>
                      save({ customFields: { ...custom(), [key]: e.currentTarget.value } })
                    }
                  />
                </label>
              )}
            </For>
          </Show>
        </fieldset>
      </div>
    </Show>
  );
};

const CreateExpedienteCta: Component<{
  canManage: boolean;
  onCreate: () => void;
}> = (props) => {
  const { t } = useI18n();

  return (
    <div class="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-border p-10 text-center">
      <IdCard class="h-10 w-10 text-muted-foreground" />
      <p class="text-sm text-muted-foreground">{t(msg`This member has no academic record yet.`)}</p>
      <Show when={props.canManage}>
        <Button data-testid="ficha-create" onClick={() => props.onCreate()} class="gap-2">
          <GraduationCap class="h-4 w-4" /> {t(msg`Create record`)}
        </Button>
      </Show>
    </div>
  );
};
